import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {selectWisbit} from 'store/selectors';
import {OPERATE_TYPES} from 'api/constants';
import {MODE} from 'containers/App/constants';
import {Title, Input, TextArea, Label} from 'components';

import {
  changeTitle,
  changeDate,
  changeDescription,
  changeThumbnail,
} from './redux/actions';
import {saveWisbit} from './redux/thunks';
import {Wisbit} from './types';
import {FIELDS} from './constants';
import i18n from 'i18next';

import ImageUploaderField from 'components/ImageUploaderField';
import {TagsInputField} from 'components/TagsInputField/TagsInputField';
import {getTags} from 'api/requests/tagRequests';
import {Tag} from 'api/types/tagTypes';
import {uploadImage} from 'utils';

type GeneralInformationProps = {
  mode: string;
  errorMessage?: (field: string) => string | undefined;
};

const GeneralInformation = ({mode, errorMessage}: GeneralInformationProps) => {
  const {title, description, thumbnailId, tags} = useSelector(selectWisbit);

  const [allTags, setAllTags] = useState<Tag[]>();

  const dispatch = useDispatch();

  // Effects

  useEffect(() => {
    getTags()
      .then(setAllTags)
      .catch(e => console.error(e));
  }, []);

  // Event handlers

  const onUpdate =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const {value} = event.target;

      if (field === FIELDS.TITLE) {
        dispatch(changeTitle(value));
      } else if (field === FIELDS.DATE) {
        dispatch(changeDate(value));
      }
    };

  const onSave = (field: keyof Wisbit) => () => {
    dispatch(saveWisbit({op: OPERATE_TYPES.REPLACE, field}));
  };

  const onUpload = async (file: File) => {
    try {
      const newImageId = await uploadImage(file);

      dispatch(
        saveWisbit({
          op: OPERATE_TYPES.REPLACE,
          field: FIELDS.THUMBNAIL,
          thumbnailId: newImageId,
        })
      );
      dispatch(changeThumbnail(newImageId));
    } catch (e) {
      console.error(e);
    }
  };

  const addTagHandler = (tagId: string) => {
    dispatch(
      saveWisbit({
        op: OPERATE_TYPES.ADD,
        field: FIELDS.TAGS,
        tagId,
        tagIndex: '-',
      })
    );
  };

  const deleteTagHandler = (tagId: string, tagIndex: number) => {
    dispatch(
      saveWisbit({
        op: OPERATE_TYPES.REMOVE,
        field: FIELDS.TAGS,
        tagId,
        tagIndex,
      })
    );
  };

  const disabled = mode === MODE.VIEW;

  return (
    <form className="flex flex-col w-1/3 pr-10" style={{minWidth: '350px'}}>
      <Title className="pt-8">
        {i18n.t('wisbitPage.generalInformation.title')}
      </Title>
      <Label htmlFor={FIELDS.TITLE} label="Titel" className="mt-6">
        <Input
          id={FIELDS.TITLE}
          value={title}
          onChange={onUpdate(FIELDS.TITLE)}
          onBlur={onSave(FIELDS.TITLE)}
          disabled={disabled}
          maxLength={50}
          errorMessage={errorMessage && errorMessage(FIELDS.TITLE)}
        />
      </Label>
      <Label
        htmlFor={FIELDS.DESC}
        label={i18n.t('wisbitPage.generalInformation.description')}
        className="mt-4"
      >
        <TextArea
          id={FIELDS.DESC}
          value={description}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            dispatch(changeDescription(e.target.value))
          }
          onBlur={onSave(FIELDS.DESC)}
          disabled={disabled}
          maxLength={500}
          errorMessage={errorMessage && errorMessage(FIELDS.DESC)}
        />
      </Label>
      {allTags && (
        <Label
          htmlFor={FIELDS.TAGS}
          label={i18n.t('wisbitPage.generalInformation.tagsLabel')}
          className="mt-4"
        >
          <TagsInputField
            tags={tags || []}
            allTags={allTags}
            disabled={disabled}
            allowInput={(tags || []).length < 3}
            onNewTag={addTagHandler}
            onDeleteTag={deleteTagHandler}
            errorMessage={errorMessage && errorMessage(FIELDS.TAGS)}
          />
        </Label>
      )}
      <Label
        htmlFor={FIELDS.THUMBNAIL}
        label={i18n.t('wisbitPage.generalInformation.image')}
        className="mt-4"
      >
        <ImageUploaderField
          id={FIELDS.THUMBNAIL}
          imageId={thumbnailId}
          onChange={onUpload}
          disabled={disabled}
          errorMessage={errorMessage && errorMessage(FIELDS.THUMBNAIL)}
        />
      </Label>
      {/*
      // TODO: Will add in the future once the backend supports
      <Label htmlFor={FIELDS.DATE} label="Due date" className="mt-4">
        <Input
          id={FIELDS.DATE}
          type="date"
          value={date}
          onChange={onUpdate(FIELDS.DATE)}
          disabled={disabled}
          min={format(new Date(), 'yyyy-MM-dd')}
          errorMessage={errorMessage && errorMessage(FIELDS.DATE)}
        />
      </Label> */}
    </form>
  );
};

export default GeneralInformation;
